import { Color } from '@cadstrom/renderer';
import { createContext, useEffect, useMemo, useState } from 'react';

import type { GraphicRenderOpts, GraphicRenderStateOpts } from '@cadstrom/renderer';
import type { PcbLayerType, SchLayerType } from '@cadstrom/sigma-engine-shared';
import type { CadstromColorProvider } from '../color-provider/cadstrom-color-provider.js';

export interface CadstromColorProviderContextValue {
	schematic: CadstromColorProvider;
	pcb: CadstromColorProvider;
	/**
	 * Shared providers built from the core model's real layer sets, keyed by layer set id. Empty
	 * when no core model is in scope.
	 */
	byLayerSet: ReadonlyMap<string, CadstromColorProvider>;
}

export const CadstromColorProviderContext = createContext<CadstromColorProviderContextValue | null>(null);

/**
 * Resolves the render-state colour options for a layer type from the given colour provider, and
 * re-renders whenever the colour changes (via the options' `changeWatcher` `changed` event or a
 * theme-mode switch).
 *
 * @param provider - The colour provider to look the layer up in, or `undefined` when no provider
 *   context is mounted.
 * @param type - The layer type to look up.
 * @returns The colour options for the layer type, or `undefined` when there is no provider or the
 *   type has no layer.
 */
export function useColorForLayer(
	provider: CadstromColorProvider | undefined,
	type: SchLayerType | PcbLayerType,
): GraphicRenderStateOpts | undefined {
	const baseOpts = useMemo(() => {
		if (!provider) {
			return undefined;
		}

		for (const layer of provider.providerLayers.values()) {
			if (layer.type === type) {
				return provider.getColorForLayer(layer.id);
			}
		}

		return undefined;
	}, [provider, type]);

	// Watched updates are keyed by the opts object they came from, so a stale update from a
	// previous provider/type is ignored instead of needing a state reset in the effect.
	const [watched, setWatched] = useState<{ source: GraphicRenderStateOpts; value: GraphicRenderStateOpts }>();

	useEffect(() => {
		const watcher = baseOpts?.changeWatcher;
		if (!baseOpts || !watcher) {
			return;
		}

		// setLayerColor mutates and re-emits the same opts object, so spread to get a fresh
		// identity that triggers a re-render.
		const handleChanged = (next: GraphicRenderStateOpts) => setWatched({ source: baseOpts, value: { ...next } });
		watcher.on('changed', handleChanged);
		return () => {
			watcher.removeListener('changed', handleChanged);
		};
	}, [baseOpts]);

	if (watched !== undefined && watched.source === baseOpts) {
		return watched.value;
	}

	return baseOpts;
}

/**
 * Converts render options to a CSS colour string.
 *
 * @param opts - The tint/alpha pair to convert.
 * @returns A CSS colour string usable in inline styles.
 */
export function graphicRenderOptsToCss(opts: GraphicRenderOpts): string {
	return Color.default(opts.tint).alpha(opts.alpha).toString();
}
